import type { Query } from '@tanstack/vue-query'
import { useApi } from '~/api'
import { defineService } from '~/api/models/helpers'
import type { PaginatedResult, PaginationParams, ScheduleQueryParams } from '~/types/pagination'
import type {
  Schedule,
  EventCreatePayload,
  UpdateSchedulePayload,
  SyncStatusFilter,
  CommStatusFilter,
  ScheduleAssetDTO,
  ScheduleAssetSyncStatus
} from '~/types/schedule'
import type { WithId } from '~/types/common'
import type { AuthArgs } from '~/api/models/index'
import { getAssetStatusCount } from '~/utils/schedule'

interface BaseEventPayload {
  scheduleId: number
  payload: EventCreatePayload
}

const SYNC_POLL_INTERVAL_MS = 3000
const IN_FLIGHT_SYNC_STATUSES: ScheduleAssetSyncStatus[] = ['PROCESSING', 'SENDING', 'SENT']

export const isScheduleSyncInFlight = (schedule?: Schedule): boolean =>
  !!schedule && IN_FLIGHT_SYNC_STATUSES.some((status) => getAssetStatusCount(schedule, status) > 0)

export const hasAssetsSyncInFlight = (assets?: ScheduleAssetDTO[]): boolean =>
  !!assets?.some((asset) => IN_FLIGHT_SYNC_STATUSES.includes(asset.syncStatus))

const queries = {
  getAll: {
    queryFn: (pagination: Ref<ScheduleQueryParams>) => useApi().schedules.getSchedules({ ...pagination.value }),
    queryKey: (pagination: Ref<ScheduleQueryParams>) => [
      'allSchedules',
      {
        current_page: pagination.value.current_page,
        per_page: pagination.value.per_page,
        sort: pagination.value.sort,
        filter: pagination.value.filter,
        search_terms: pagination.value.search_terms,
        site_id: pagination.value.site_id
      }
    ],
    options: {
      staleTime: () => 1000 * 60 * 5,
      refetchOnMount: () => true
    }
  },
  getById: {
    queryFn: (args: { id: number }) => useApi().schedules.getScheduleById(args.id),
    queryKey: (args: { id: number }) => ['scheduleById', String(args.id)],
    options: {
      refetchInterval:
        () =>
        (query: Query<Schedule>): number | false =>
          isScheduleSyncInFlight(query.state.data) ? SYNC_POLL_INTERVAL_MS : false
    }
  },
  getByMonth: {
    queryFn: (args: { month: number; year: number }) => useApi().schedules.getSchedulesByMonth(args.year, args.month),
    queryKey: (args: { month: number; year: number }) => ['schedulesByMonth', String(args.month), String(args.year)]
  },
  getEventsByMonth: {
    queryFn: (args: { month: number; year: number; siteId?: number }) =>
      useApi().schedules.getScheduleEventsByMonth(args.year, args.month, args.siteId),
    queryKey: (args: { month: number; year: number; siteId?: number }) => [
      'scheduleEventsByMonth',
      String(args.month),
      String(args.year),
      { site_id: args.siteId }
    ]
  },
  getScheduleEvents: {
    queryFn: (args: Ref<PaginationParams & { id: number }>) =>
      useApi().schedules.getScheduleEventsByScheduleId(args.value.id, {
        per_page: args.value.per_page,
        current_page: args.value.current_page
      }),
    queryKey: (args: Ref<PaginationParams & { id: number }>) => [
      'scheduleEvents',
      {
        id: args.value.id,
        current_page: args.value.current_page,
        per_page: args.value.per_page,
        sort: args.value.sort
      }
    ],
    options: {
      enabled: (args: Ref<PaginationParams & { id: number }>) => !!args.value.id
    }
  },
  getEventById: {
    queryFn: (args: { id?: number; scheduleId: number }) =>
      useApi().schedules.getScheduleEventById(args.scheduleId, args.id || 0),
    queryKey: (args: { id?: number; scheduleId: number }) => ['eventById', String(args.id || '')],
    options: {
      enabled: (args: { id?: number; scheduleId: number }) => !!args.id && !!args.scheduleId
    }
  },
  getAssetsByScheduleId: {
    queryFn: (
      args: Ref<
        Omit<PaginationParams, 'status'> & {
          id: number
          sync_status?: SyncStatusFilter[]
          comm_status?: CommStatusFilter[]
          group_ids?: string[]
          zone_ids?: string[]
        }
      >
    ) =>
      useApi().schedules.getScheduleAssetsByScheduleId(args.value.id, {
        per_page: args.value.per_page,
        current_page: args.value.current_page,
        sort: args.value.sort,
        sync_status: args.value.sync_status,
        comm_status: args.value.comm_status,
        group_ids: args.value.group_ids,
        zone_ids: args.value.zone_ids
      }),
    queryKey: (
      args: Ref<
        Omit<PaginationParams, 'status'> & {
          id: number
          sync_status?: SyncStatusFilter[]
          comm_status?: CommStatusFilter[]
          group_ids?: string[]
          zone_ids?: string[]
        }
      >
    ) => [
      'assetsByScheduleId',
      {
        id: args.value.id,
        current_page: args.value.current_page,
        per_page: args.value.per_page,
        sort: args.value.sort,
        sync_status: args.value.sync_status,
        comm_status: args.value.comm_status,
        group_ids: args.value.group_ids,
        zone_ids: args.value.zone_ids
      }
    ],
    options: {
      refetchInterval:
        () =>
        (query: Query<PaginatedResult<ScheduleAssetDTO>>): number | false =>
          hasAssetsSyncInFlight(query.state.data?.data) ? SYNC_POLL_INTERVAL_MS : false
    }
  },
  getCapabilitiesById: {
    queryFn: (args: { id: number }) => useApi().schedules.getScheduleCapabilitiesByScheduleId(args.id),
    queryKey: (args: { id: number }) => ['scheduleCapabilitiesById', String(args.id)]
  }
} as const

const mutations = {
  create: {
    request: (payload: Partial<Schedule>) => useApi().schedules.createSchedule(payload),
    cacheKey: (_payload: Partial<Schedule>) => [['allSchedules'], ['groupDetails']]
  },
  update: {
    request: ({ id, ...payload }: WithId<UpdateSchedulePayload>) => useApi().schedules.updateSchedule(id, payload),
    cacheKey: ({ id }: WithId<UpdateSchedulePayload>) => [
      ['allSchedules'],
      ['scheduleById', String(id)],
      ['assetsByScheduleId'],
      ['scheduleCapabilitiesById', String(id)],
      ['groupDetails']
    ]
  },
  delete: {
    request: (id: number) => useApi().schedules.deleteSchedule(id),
    cacheKey: (id: number) => [
      ['allSchedules'],
      ['scheduleById', String(id)],
      ['schedulesByMonth'],
      ['scheduleEventsByMonth'],
      ['groupDetails']
    ]
  },
  createEvent: {
    request: (args: BaseEventPayload) => useApi().schedules.createScheduleEvent(args.scheduleId, args.payload),
    cacheKey: (args: BaseEventPayload) => [
      ['scheduleEvents'],
      ['scheduleEventsByMonth'],
      ['scheduleById', String(args.scheduleId)]
    ]
  },
  updateEvent: {
    request: (args: WithId<BaseEventPayload>) =>
      useApi().schedules.updateScheduleEvent(args.scheduleId, args.id, args.payload),
    cacheKey: (args: WithId<BaseEventPayload>) => [
      ['scheduleEvents'],
      ['scheduleEventsByMonth'],
      ['scheduleById', String(args.scheduleId)],
      ['eventById', String(args.id)]
    ]
  },
  deleteEvent: {
    request: (args: { id: number; scheduleId: number }) =>
      useApi().schedules.deleteScheduleEvent(args.scheduleId, args.id),
    // Don't invalidate ['eventById', id] — EventModal still observes it and would 404.
    cacheKey: (args: { id: number; scheduleId: number }) => [
      ['scheduleEvents'],
      ['scheduleEventsByMonth'],
      ['scheduleById', String(args.scheduleId)]
    ]
  },
  retryFailed: {
    request: (args: { scheduleId: number }) => useApi().schedules.retryFailedAssets(args.scheduleId),
    cacheKey: (args: { scheduleId: number }) => [
      ['scheduleById', String(args.scheduleId)],
      ['assetsByScheduleId'],
      ['allSchedules'],
      ['schedulesByMonth']
    ]
  }
} as const

export type ScheduleService = ReturnType<typeof defineService<typeof queries, typeof mutations>>

export const registerService = (authArgs: AuthArgs): ScheduleService =>
  defineService(authArgs, {
    queries,
    mutations
  })
